import { supabase } from './supabase';
import { CLIENTS_CACHE_KEY } from './adminCache';

export function clientSnapshotKey(clientId) {
  return `client.${clientId}.snapshot.v2`;
}

export function regionTeamsCacheKey(clientId, regionId) {
  return `client.${clientId}.region.${regionId}.teams.v1`;
}

export function invalidateClientSnapshot(clientId) {
  try {
    localStorage.removeItem(clientSnapshotKey(clientId));
  } catch {}
}

export function invalidateRegionTeamsCache(clientId, regionId) {
  try {
    localStorage.removeItem(regionTeamsCacheKey(clientId, regionId));
  } catch {}
}

export function updateClientsListCache(clientId, businessName) {
  try {
    const raw = localStorage.getItem(CLIENTS_CACHE_KEY);
    if (!raw) return;
    const parsed = JSON.parse(raw);
    const list = Array.isArray(parsed?.data) ? parsed.data : [];
    const updated = list.map((c) => (
      String(c.id) === String(clientId) ? { ...c, business_name: businessName } : c
    ));
    localStorage.setItem(CLIENTS_CACHE_KEY, JSON.stringify({ savedAt: Date.now(), data: updated }));
  } catch {}
}

export function removeClientFromListCache(clientId) {
  try {
    const raw = localStorage.getItem(CLIENTS_CACHE_KEY);
    if (!raw) return;
    const parsed = JSON.parse(raw);
    const list = Array.isArray(parsed?.data) ? parsed.data : [];
    const filtered = list.filter((c) => String(c.id) !== String(clientId));
    localStorage.setItem(CLIENTS_CACHE_KEY, JSON.stringify({ savedAt: Date.now(), data: filtered }));
  } catch {}
}

export async function deleteClientById(clientId) {
  const { data: allTeams, error: teamsErr } = await supabase
    .from('teams')
    .select('id')
    .eq('client_id', clientId);
  if (teamsErr) throw new Error('Failed to fetch teams for client');

  const teamIds = (allTeams || []).map((t) => t.id);
  if (teamIds.length > 0) {
    const { data: bootcampsForTeams } = await supabase
      .from('bootcamps')
      .select('id')
      .in('team_id', teamIds);
    const bootcampIds = (bootcampsForTeams || []).map((b) => b.id);
    if (bootcampIds.length > 0) {
      await supabase.from('lesson_completions').delete().in('bootcamp_id', bootcampIds);
      await supabase.from('user_reflections').delete().in('bootcamp_id', bootcampIds);
      await supabase.from('user_points').delete().in('bootcamp_id', bootcampIds);
      await supabase.from('bootcamps').delete().in('id', bootcampIds);
    }
    await supabase.from('team_users').delete().in('team_id', teamIds);
    await supabase.from('teams').delete().eq('client_id', clientId);
  }

  const { data: allRegions } = await supabase
    .from('regions')
    .select('id')
    .eq('client_id', clientId);
  const regionIds = (allRegions || []).map((r) => r.id);
  if (regionIds.length > 0) {
    await supabase.from('admin_user_regions').delete().in('region_id', regionIds);
  }
  await supabase.from('regions').update({ active: false }).eq('client_id', clientId);
  await supabase.from('regions').delete().eq('client_id', clientId);
  await supabase.from('users').update({ client_id: null }).eq('client_id', clientId);
  await supabase.from('admin_user_clients').delete().eq('client_id', clientId);

  const { error: clientDelErr } = await supabase
    .from('clients')
    .delete()
    .eq('id', clientId);
  if (clientDelErr) throw new Error('Failed to delete client');

  invalidateClientSnapshot(clientId);
  removeClientFromListCache(clientId);
}

export async function suggestTeamName(clientId, regionId, regionName) {
  try {
    const { data: regionTeamsAll } = await supabase
      .from('teams')
      .select('name, active')
      .eq('client_id', clientId)
      .eq('region_id', regionId);
    const extractNum = (n) => {
      if (!n || typeof n !== 'string') return 0;
      const m = n.match(/(\d+)\s*$/);
      return m ? parseInt(m[1], 10) : 0;
    };
    const maxNum = Math.max(0, ...((regionTeamsAll || []).map((t) => extractNum(t.name))));
    return `${regionName} Team ${maxNum + 1}`;
  } catch {
    return `${regionName} Team`;
  }
}
